/**
 * "FROM THE ESTATE PLAN" zone (§1.6 Build 4b-2, D-123.10/11/17). Renders a task's A/B/E
 * bindings: Class A record sub-items (with per-record check-off), Class B labelled facts,
 * Class E sweep figures + Financial Summary link. Cat A cells → secure-file pointer; empty
 * A/B → explicit gap line; lazy fetch → quiet Loading line. The zone hides entirely when
 * the task has no A/B/E bindings.
 */
import { useState } from "react";
import { Link } from "wouter";
import { ArrowRight, Lock } from "lucide-react";
import type { ResolvedBinding } from "@shared/task-bindings";
import { RecordRow } from "./RecordRow";
import { deriveRecordStates, deriveLetterStates, recordProgress, taskVerb, sectionNoun, planDeepLink, cellText, type TaskEventLite, type RecordState, type LetterState } from "./binding-helpers";
import { SAGE, WARM } from "@shared/brand";


const FIGURE_LABELS: Record<string, string> = {
  totalValue: "Total assets",
  probateValue: "Probate value",
  nonProbateValue: "Outside estate",
  taxAmount: "Estate Administration Tax",
  netEstateValue: "Net estate",
  assetsTotal: "Total assets",
  liabilitiesTotal: "Total debts",
  eatAmount: "Estate Administration Tax",
  monthlyNet: "Monthly net",
  formType: "Form",
  isSmallEstate: "Small estate",
};
const MONEY_KEYS = new Set(["totalValue", "probateValue", "nonProbateValue", "taxAmount", "netEstateValue", "assetsTotal", "liabilitiesTotal", "eatAmount", "monthlyNet"]);

function fig(key: string, val: number | string): string {
  if (MONEY_KEYS.has(key) && typeof val === "number") return `$${Math.round(val).toLocaleString()}`;
  return String(val);
}

const LABEL_STYLE = { color: SAGE[600], fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" } as const;
// s166 (D-166.7): the class-B section lead-in. Same register as the zone headers (FROM THE ESTATE
// PLAN / WHO CAN HELP) so it reads as part of the same system, one step quieter — taupe rather than
// sage, 10px rather than 11 — because it groups facts INSIDE a zone rather than naming the zone.
const SUBGROUP_LABEL_STYLE = { color: WARM.taupe, fontSize: 10, fontWeight: 600, letterSpacing: "0.06em" } as const;

// D-130.8: a Class-A record list caps its RENDER at 6 rows and shows an overflow line ("…and N
// more") with a Show-all (expand in place) affordance + a deep link to the source section. The
// resolver still returns ALL rows — the progress fraction + record_action state upstream count
// every row; only the render is capped. Cap ONLY when rows > 7 (capping exactly 7 is sillier than
// showing 7). One shared mechanism for every A binding (G7).
const A_ROW_CAP = 6;
function ARecordList({ b, clientId, taskKey, states, letterStates, verb }: {
  b: Extract<ResolvedBinding, { class: "A" }>;
  clientId: string;
  taskKey: string;
  states: Map<string, RecordState>;
  letterStates: Map<string, LetterState>;
  verb: ReturnType<typeof taskVerb>;
}) {
  const [showAll, setShowAll] = useState(false);
  const capped = b.rows.length > 7 && !showAll;
  const shown = capped ? b.rows.slice(0, A_ROW_CAP) : b.rows;
  const hidden = b.rows.length - A_ROW_CAP;
  const link = planDeepLink(b.categorySlug, b.section);
  return (
    <div className="flex flex-col gap-0.5">
      {shown.map((row) => (
        <RecordRow key={row.rowId} clientId={clientId} taskKey={taskKey} section={b.section}
          sectionName={b.sectionName} categorySlug={b.categorySlug} row={row} state={states.get(row.rowId)} verb={verb}
          letterTemplateType={b.letterTemplateType}
          letterState={row.letterInstanceKey ? letterStates.get(row.letterInstanceKey) : undefined} />
      ))}
      {capped && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-1 pt-1 text-[13px]" style={{ color: WARM.taupe }} data-testid="a-overflow">
          <span>…and {hidden} more</span>
          <button type="button" onClick={() => setShowAll(true)} className="underline font-medium" style={{ color: SAGE[600] }} data-testid="a-show-all">Show all</button>
          {link && (
            <>
              <span aria-hidden>·</span>
              {/* s162 Defect C: client-side nav — a raw href reloads the app and flashes the
                  executor's 0% progress skeleton before the destination renders. */}
              <Link href={link} className="underline font-medium" style={{ color: SAGE[600] }} data-testid="a-overflow-link">open {sectionNoun(b.section, b.sectionName)} <ArrowRight className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" /></Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function EstatePlanZone({
  bindings, clientId, taskKey, events, isLoading,
}: {
  bindings: ResolvedBinding[];
  clientId: string;
  taskKey: string;
  events: TaskEventLite[];
  isLoading: boolean;
}) {
  const planBindings = bindings.filter((b) => b.class === "A" || b.class === "B" || b.class === "E");
  // Zone renders during load (so the label + skeleton show) OR when there are A/B/E bindings.
  if (!isLoading && planBindings.length === 0) return null;

  const states = deriveRecordStates(events);
  const letterStates = deriveLetterStates(events);
  const verb = taskVerb(taskKey);

  // Progress fraction across all Class A bindings (D-123.10).
  const aRows = bindings.flatMap((b) => (b.class === "A" ? b.rows : []));
  const progress = recordProgress(aRows, states);
  const fraction = aRows.length > 0 ? ` · ${progress.done} of ${progress.total} ${verb.past}` : "";

  return (
    <div className="mt-4" data-testid="estate-plan-zone">
      <div className="uppercase mb-2" style={LABEL_STYLE}>From the estate plan{isLoading ? "" : fraction}</div>

      {isLoading ? (
        <div className="text-[13px]" style={{ color: WARM.taupe }} data-testid="bindings-loading">Loading…</div>
      ) : (
        <div className="flex flex-col gap-3">
          {planBindings.map((b, i) => {
            if (b.class === "A") {
              if (b.rows.length === 0) {
                return <div key={i} className="text-[13px]" style={{ color: WARM.taupe }} data-testid="gap-line">No {sectionNoun(b.section, b.sectionName)} were recorded.</div>;
              }
              return <ARecordList key={i} b={b} clientId={clientId} taskKey={taskKey} states={states} letterStates={letterStates} verb={verb} />;
            }
            if (b.class === "B") {
              const shown = b.cells.filter((c) => c.secureFilePointer || (c.value != null && c.value !== ""));
              if (shown.length === 0) {
                return <div key={i} className="text-[13px]" style={{ color: WARM.taupe }} data-testid="gap-line">No {sectionNoun(b.section, b.sectionName)} were recorded.</div>;
              }
              // s166 (D-166.7): class-B facts describe the SECTION, not any record — the deceased's
              // date of birth, the will's location. Rendered as a bare list they sat flush under the
              // last class-A record row and read as that record's detail (the reported shape: the
              // deceased's DOB looking like it belonged to the second beneficiary). The lead-in names
              // where the facts come from, so a section-level fact can never be mistaken for record
              // detail again. One lead-in per B binding, so facts from different sections group
              // separately. The name comes from the resolved section definition (`sectionName`,
              // loaded by resolveB) — never a hardcoded string, so it localizes on the same path as
              // every other section label. Quieter than the zone headers: this is a sub-grouping.
              return (
                <div key={i} className="flex flex-col gap-1">
                  <div className="uppercase" style={SUBGROUP_LABEL_STYLE} data-testid={`fact-group-${b.section}`}>{b.sectionName}</div>
                  {shown.map((c) => (
                    <div key={c.fieldKey} className="text-[14px]" style={{ color: WARM.charcoal }} data-testid={`fact-${c.fieldKey}`}>
                      {c.label}:{" "}
                      {c.secureFilePointer
                        ? <span className="inline-flex items-center gap-0.5" style={{ color: WARM.taupe }}><Lock className="h-3 w-3" /> in the secure file</span>
                        : <span style={{ fontWeight: 500 }}>{cellText(c)}</span>}
                    </div>
                  ))}
                </div>
              );
            }
            // Class E — sweep figure strip + Financial Summary link (D-123.17).
            // D-130.5: an optional one-line caption explains WHY the figure appears.
            return (
              <div key={i} data-testid="sweep-figures">
                {b.caption ? (
                  <div className="text-[13px] mb-1" style={{ color: WARM.taupe }} data-testid="sweep-caption">{b.caption}</div>
                ) : null}
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  {Object.entries(b.figures).map(([k, v]) => (
                    <div key={k} className="text-[13px]">
                      <span style={{ color: WARM.taupe }}>{FIGURE_LABELS[k] ?? k}: </span>
                      <span style={{ color: WARM.charcoal, fontWeight: 500 }} className="tabular-nums">{fig(k, v)}</span>
                    </div>
                  ))}
                </div>
                {/* s162 Defect C: same fix — this one also stays inside the executor shell. */}
                <Link href={b.link} className="text-[13px] font-medium inline-block mt-2" style={{ color: SAGE[600] }} data-testid="open-financial-summary">
                  Open Financial Summary <ArrowRight className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" />
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
